// RisikenTabelleRedesign.tsx — eigenständige Risiken-Seite (/risiken) im
// „Refined Classic"-Redesign auf der generischen DataTable-Engine.
//
// Spalten: Typ · Risikoobjekt · Kontakt · Vertrag · dann die Verträge-Spalten
// (Vertragsart · Verantwortlichkeit · Bezeichnung · Gesellschaft (Logo) · Status
// · Prämie · Beginn · Ablauf) aus dem verknüpften Vertrag.
// Nur echte Objekt-Kategorien (objektOnly). Scope-Toggle, serverseitige Suche
// (über Risiko + Vertragsnummer + Kunde), Mobile-Karten.

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { I } from "./kvpIcons";
import { DataTable, ColumnsMenu, type Column, type SortState, type DateRangeValue, Dash } from "./table";
import { buildRiskFilters } from "./riskFilterParams";
import "./contactRedesign.css";
import { apiFetch } from "../../utils/apiFetch";
import { defaultContactScope } from "../../utils/contactScope";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { useInfiniteRows } from "../../hooks/useInfiniteRows";
import { useResponsive } from "../../hooks/useResponsive";
import { useAuth } from "../../context/AuthContext";
import type { ContactScope } from "../contacts/ContactScopeToggle";
import {
  KontaktPill,
  VerantwortlichkeitCell,
  GesellschaftLogo,
  contactName,
  formatEuro,
  formatDate,
  produktBezeichnung,
  riskObjectDisplay,
} from "./contractRiskCells";

type Row = any;

const pickContact = (r: Row) =>
  r?.contract?.contact ??
  (Array.isArray(r?.contacts) && r.contacts.length > 0 ? r.contacts[0] : null) ??
  r?.directContact ??
  null;
const premiumOf = (c: any) => (c?.premiumAnnualGross ?? c?.annualFee ?? null);

const RisikenTabelleRedesign: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, teams } = useAuth();
  const hasTeam = (teams?.length ?? 0) > 0;
  const { isPhone } = useResponsive();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [sort, setSort] = useState<SortState | null>(null);
  const [scope, setScope] = useState<ContactScope>(() => defaultContactScope(isAdmin, hasTeam));
  useEffect(() => {
    if (scope === "team" && !hasTeam) setScope("all");
  }, [scope, hasTeam]);

  const scopeOptions = useMemo(() => {
    const opts: { value: ContactScope; label: string }[] = [{ value: "all", label: "Alle" }];
    if (hasTeam) opts.push({ value: "team", label: "Team" });
    opts.push({ value: "own", label: "Persönlich" });
    return opts;
  }, [hasTeam]);

  // Spaltenfilter (serverseitig).
  const [filterValues, setFilterValues] = useState<Record<string, string | boolean | undefined>>({});
  const [dateRangeValues, setDateRangeValues] = useState<Record<string, DateRangeValue | undefined>>({});
  const [facets, setFacets] = useState<{ typ: string[]; status: string[]; gesellschaft: string[]; vertragsart: string[] }>({
    typ: [],
    status: [],
    gesellschaft: [],
    vertragsart: [],
  });
  useEffect(() => {
    apiFetch<any>("risk-objects/facets")
      .then(({ data }) => data && setFacets(data))
      .catch(() => {});
  }, []);
  const updateFilter = useCallback(
    (id: string, v: string | boolean | undefined) => setFilterValues((p) => ({ ...p, [id]: v })),
    [],
  );
  const updateDateRange = useCallback(
    (id: string, r: DateRangeValue) => setDateRangeValues((p) => ({ ...p, [id]: r })),
    [],
  );
  const riskFilters = useMemo(
    () => buildRiskFilters({ filterValues, dateRangeValues }),
    [filterValues, dateRangeValues],
  );

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>([]);
  const toggleCollapsed = useCallback(
    (id: string) => setCollapsedColumns((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])),
    [],
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const { rows, hasMore, loading, loadMore } = useInfiniteRows<Row>({
    getKey: (r) => r.id,
    deps: [debouncedSearch, sort?.field ?? "", sort?.direction ?? "", scope, JSON.stringify(riskFilters)],
    fetchPage: async ({ limit, offset, signal }) => {
      const hasFilters = Object.keys(riskFilters).length > 0;
      const { data, total } = await apiFetch<any[]>("risk-objects", {
        signal,
        params: {
          limit,
          offset,
          q: debouncedSearch,
          objektOnly: true,
          scope,
          sort: sort?.field ?? "",
          direction: sort?.direction ?? "",
          ...(hasFilters ? { filters: JSON.stringify(riskFilters) } : {}),
        },
      });
      return { rows: Array.isArray(data) ? data : [], total };
    },
  });

  const openRisk = (r: Row) => navigate(`/risks/${r.id}`);

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        id: "Typ",
        header: "Typ",
        pinned: true,
        frozen: true,
        sortable: true,
        sortField: "riskType",
        minWidth: 120,
        filter: { kind: "dropdown", options: facets.typ },
        cell: (r) => r.riskType || <Dash />,
      },
      {
        id: "Risikoobjekt",
        header: "Risikoobjekt",
        pinned: true,
        frozen: true,
        sortable: true,
        sortField: "name",
        minWidth: 220,
        resizable: true,
        filter: { kind: "text" },
        cell: (r) => riskObjectDisplay(r) || <Dash />,
      },
      {
        id: "Kontakt",
        header: "Kontakt",
        minWidth: 200,
        resizable: true,
        filter: { kind: "text" },
        cell: (r) => <KontaktPill contact={pickContact(r)} />,
      },
      {
        id: "Vertrag",
        header: "Vertrag",
        minWidth: 140,
        resizable: true,
        cell: (r) => r.contract?.contractNbr || <Dash />,
      },
      {
        id: "Vertragsart",
        header: "Vertragsart",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        filter: { kind: "dropdown", options: facets.vertragsart },
        cell: (r) => r.contract?.insuranceProduct?.name || r.contract?.branch || <Dash />,
      },
      {
        id: "Verantwortlichkeit",
        header: "Verantwortlichkeit",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        cell: (r) => <VerantwortlichkeitCell contact={pickContact(r)} />,
      },
      {
        id: "Bezeichnung",
        header: "Bezeichnung",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        cell: (r) => produktBezeichnung(r.contract) || <Dash />,
      },
      {
        id: "Gesellschaft",
        header: "Gesellschaft",
        hideable: true,
        width: 200,
        resizable: true,
        filter: { kind: "dropdown", options: facets.gesellschaft },
        cell: (r) => (
          <GesellschaftLogo
            id={r.contract?.gesellschaftId ?? r.contract?.gesellschaft?.id}
            name={r.contract?.gesellschaft?.name}
          />
        ),
      },
      {
        id: "Status",
        header: "Status",
        hideable: true,
        collapsible: true,
        filter: { kind: "dropdown", options: facets.status },
        cell: (r) => r.contract?.contractPhase?.label || <Dash />,
      },
      {
        id: "Praemie",
        header: "Prämie",
        align: "right",
        hideable: true,
        cell: (r) => {
          const v = premiumOf(r.contract);
          return v != null ? formatEuro(v) : <Dash />;
        },
      },
      {
        id: "Beginn",
        header: "Beginn",
        hideable: true,
        collapsible: true,
        filter: { kind: "dateRange" },
        cell: (r) => formatDate(r.contract?.beginPolicyTerm) || <Dash />,
      },
      {
        id: "Ablauf",
        header: "Ablauf",
        hideable: true,
        collapsible: true,
        filter: { kind: "dateRange" },
        cell: (r) => formatDate(r.contract?.endPolicyTerm) || <Dash />,
      },
    ],
    [facets],
  );

  const scopeSeg = (
    <div className="rbac-seg" role="group" aria-label="Sichtbarkeitsbereich">
      {scopeOptions.map((o) => (
        <button
          key={o.value}
          type="button"
          className={"rbac-seg-btn" + (scope === o.value ? " on" : "")}
          aria-pressed={scope === o.value}
          onClick={() => setScope(o.value)}
        >
          <span className="rbac-dot" />
          {o.label}
        </button>
      ))}
    </div>
  );

  const tableSubBar = (
    <div className="kt-subbar">
      <ColumnsMenu
        columns={columns}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={toggleCollapsed}
      />
    </div>
  );

  const cardList = (
    <div className="ct-cards" id="risiken-scroll" ref={scrollRef}>
      {rows.length === 0 ? (
        <div className="empty">{loading ? "Lade Risiken …" : "Keine Risiken gefunden."}</div>
      ) : (
        rows.map((r) => (
          <div key={r.id} className="ct-card" onClick={() => openRisk(r)}>
            <div className="ct-card-head">
              <div className="ct-card-id">
                <div className="ct-card-name">{riskObjectDisplay(r) || "Risiko"}</div>
                <div style={{ fontSize: 12, color: "#8a7b85" }}>{r.riskType || "—"}</div>
              </div>
              <GesellschaftLogo
                id={r.contract?.gesellschaftId ?? r.contract?.gesellschaft?.id}
                name={r.contract?.gesellschaft?.name}
              />
            </div>
            <div className="ct-card-body">
              <div className="ct-row"><span className="ct-k">Kontakt</span><span className="ct-v">{contactName(pickContact(r)) || "—"}</span></div>
              <div className="ct-row"><span className="ct-k">Vertrag</span><span className="ct-v">{r.contract?.contractNbr || "—"}</span></div>
              <div className="ct-row"><span className="ct-k">Vertragsart</span><span className="ct-v">{r.contract?.insuranceProduct?.name || r.contract?.branch || "—"}</span></div>
              <div className="ct-row"><span className="ct-k">Prämie</span><span className="ct-v">{premiumOf(r.contract) != null ? formatEuro(premiumOf(r.contract)) : "—"}</span></div>
            </div>
          </div>
        ))
      )}
      {hasMore ? (
        <button className="ct-more" onClick={() => loadMore()} disabled={loading}>
          {loading ? "Lädt …" : "Mehr laden"}
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="kvp-rd kvp-rd-table-page">
      <div className="bar">
        <div className="search">
          {I.search}
          <input
            placeholder="Suche in Risiken & Verträgen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="spacer" style={{ flex: 1 }} />
        {scopeSeg}
      </div>

      {isPhone ? (
        cardList
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={openRisk}
          emptyText={loading ? "Lade Risiken …" : "Keine Risiken gefunden."}
          sort={sort}
          onSortChange={setSort}
          filterValues={filterValues}
          onFilterChange={updateFilter}
          dateRangeValues={dateRangeValues}
          onDateRangeChange={updateDateRange}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnWidths={columnWidths}
          onColumnWidthsChange={setColumnWidths}
          collapsedColumns={collapsedColumns}
          onToggleCollapsed={toggleCollapsed}
          onLoadMore={loadMore}
          hasMore={hasMore}
          loading={loading}
          scrollContainerId="risiken-scroll"
          scrollRef={scrollRef}
          aboveTable={tableSubBar}
        />
      )}
    </div>
  );
};

export default RisikenTabelleRedesign;
